<!--
  Dashboard — the "is everything OK" page. Heavier on operational health than
  Traffic; Traffic is for poking at the data, Dashboard is for telling you
  whether you need to.

  Layout follows the design pack's dashboard:

   • Four stat tiles: pipeline volume, raw window, open detections, database.
   • Ingest throughput (24 h) beside the encryption mix ring.
   • Open detections beside per-source health.

  Everything on this page is read-only. The only action is a jump into the
  work queues (ban review / detections).
-->
<script lang="ts">
	import {
		Alert,
		Button,
		Card,
		CardHeader,
		EmptyState,
		Meter,
		PageHeader,
		Skeleton,
		SkeletonGroup,
		StatGrid,
		StatTile,
		StatusDot,
		type Tone,
	} from '@navitech/ui';
	import Clock from '@lucide/svelte/icons/clock';
	import Database from '@lucide/svelte/icons/database';
	import List from '@lucide/svelte/icons/list';
	import RadioTower from '@lucide/svelte/icons/radio-tower';
	import Shield from '@lucide/svelte/icons/shield';
	import ShieldCheck from '@lucide/svelte/icons/shield-check';
	import SquareCheckBig from '@lucide/svelte/icons/square-check-big';
	import { analytics, detections, sources } from '../lib/api';
	import { createQuery } from '../lib/query.svelte';
	import SeverityBadge from '../components/SeverityBadge.svelte';
	import Sparkline from '../components/Sparkline.svelte';
	import EncryptedDonut from '../components/EncryptedDonut.svelte';
	import { fmtBytes, fmtCount, fmtRelative, sourceHealth } from '../lib/format';
	import { followLink, link } from '../lib/router';
	import { shellStats } from '../lib/shellStats.svelte';

	const detectionsQuery = createQuery(() => detections.list({ status: 'open', limit: 10 }));
	const sourcesQuery = createQuery(() => sources.list());
	const trafficQuery = createQuery(() => analytics.overview('24h'));

	// The pipeline tiles read the shell's snapshot rather than issuing their own
	// /admin/stats call — the topbar already polls it every 15 s, and asking
	// twice per page load is the kind of duplicate that quietly doubles the
	// dashboard's cost per operator. See lib/shellStats.svelte.ts.
	const stats = $derived(shellStats.stats);

	const detectionItems = $derived(detectionsQuery.data?.items ?? []);
	const sourceItems = $derived(sourcesQuery.data?.items ?? []);
	const series = $derived(trafficQuery.data?.series ?? []);
	const encrypted = $derived(trafficQuery.data?.encrypted_total ?? 0);
	const cleartext = $derived(trafficQuery.data?.cleartext_total ?? 0);
	const pendingBans = $derived(shellStats.pendingBans ?? 0);

	const dbCap = 50 * 1024 * 1024 * 1024; // 50 GB visual ceiling — tune via env later.

	// Health → the status dot (same tones as the sidebar footer) and the
	// colour of the last-seen time beside it.
	const HEALTH_TONE: Record<'healthy' | 'stale' | 'down', Tone> = {
		healthy: 'ok',
		stale: 'warn',
		down: 'err',
	};
	const HEALTH_TEXT: Record<'healthy' | 'stale' | 'down', string> = {
		healthy: 'text-ok',
		stale: 'text-warn',
		down: 'text-err',
	};

	const loading = $derived(
		detectionsQuery.status !== 'completed' || sourcesQuery.status !== 'completed',
	);
	const failed = $derived(detectionsQuery.status === 'failed' || sourcesQuery.status === 'failed');
</script>

<PageHeader title="Dashboard" description="Operations overview · refresh every 15 s in the top bar">
	{#snippet actions()}
		{#if pendingBans > 0}
			<Button href="/ban-review" variant="primary" icon={SquareCheckBig} onclick={followLink}>
				Review {pendingBans} suggestion{pendingBans === 1 ? '' : 's'}
			</Button>
		{/if}
		<Button href="/detections" onclick={followLink}>Open detections</Button>
	{/snippet}
</PageHeader>

{#if failed}
	<Alert tone="err">
		Error loading dashboard: {detectionsQuery.errorMessage || sourcesQuery.errorMessage}
	</Alert>
{:else if loading}
	<!-- Skeleton, so the page keeps its shape while the first payload lands. -->
	<SkeletonGroup label="Loading dashboard" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
		{#each [1, 2, 3, 4] as i (i)}
			<Card>
				<Skeleton width={96} height={12} />
				<Skeleton class="mt-3" width={128} height={28} />
				<Skeleton class="mt-2" width={160} height={10} />
			</Card>
		{/each}
	</SkeletonGroup>
{:else}
	{#if stats}
		{@const st = stats}
		{@const dbPct = Math.min(100, Math.round((st.db_size_bytes / dbCap) * 100))}
		<!-- Pipeline tiles. -->
		<StatGrid as="section" class="mb-4">
			<StatTile
				label="Events stored"
				icon={List}
				mono
				value={fmtCount(st.events_total)}
				hint={`${fmtCount(st.events_last_hour)} last hour · ${fmtCount(st.events_last_24h)} last 24 h`}
			/>

			<StatTile
				label="Raw window"
				icon={Clock}
				mono
				value={st.events_oldest_at ? `${fmtRelative(st.events_oldest_at)} → now` : 'empty'}
				hint="retention cutoff configured server-side"
				class="[&_[data-slot=stat-value]]:text-xl"
			/>

			<StatTile
				label="Open detections"
				icon={Shield}
				mono
				value={fmtCount(st.detections_open)}
				hint={`${fmtCount(st.detections_closed)} closed all-time`}
				class={st.detections_open === 0
					? '[&_[data-slot=stat-value]]:text-ok'
					: '[&_[data-slot=stat-value]]:text-warn'}
			/>

			<!-- StatTile has no room for a meter, so the database tile is the same
			     anatomy (a tight card: label, value, foot) laid out by hand. -->
			<Card padding="tight" class="flex flex-col gap-[6px]">
				<div class="flex items-center gap-[7px] text-xs text-text-dim">
					<Database class="size-[15px] shrink-0 text-brand" aria-hidden="true" />
					<span>Database</span>
				</div>
				<div class="font-mono text-[26px] font-bold tracking-[0.3px]">{fmtBytes(st.db_size_bytes)}</div>
				<Meter
					label="Database size"
					hideLabel
					size="thin"
					value={dbPct}
					tone={dbPct > 80 ? 'err' : dbPct > 50 ? 'warn' : 'ok'}
					class="mt-0.5"
				/>
				<div class="text-xs text-text-faint">
					{dbPct}% of 50 GB ceiling · {fmtBytes(st.db_freelist_bytes)} freelist ·
					{#if st.aggregator_built_at}
						agg built {fmtRelative(st.aggregator_built_at)}
					{:else}
						aggregator not yet built
					{/if}
				</div>
			</Card>
		</StatGrid>
	{/if}

	<!-- Throughput + encryption mix. -->
	<section class="mb-4 grid gap-4 lg:grid-cols-[1fr_320px]">
		<Card>
			<CardHeader title="Ingest throughput" description="last 24 h" level={2}>
				{#snippet actions()}
					<Button href="/traffic" size="sm" variant="ghost" onclick={followLink}>Open traffic →</Button>
				{/snippet}
			</CardHeader>
			{#if trafficQuery.status === 'failed'}
				<p class="text-[13px] text-text-faint">Throughput unavailable: {trafficQuery.errorMessage}</p>
			{:else if series.length === 0}
				<p class="py-8 text-center text-[13px] text-text-dim">No sessions in the last 24 h.</p>
			{:else}
				<Sparkline {series} height={168} />
			{/if}
		</Card>

		<Card>
			<CardHeader title="Encryption mix" description="24 h" level={2} />
			<EncryptedDonut {encrypted} {cleartext} />
		</Card>
	</section>

	<!-- Work queues: what is open, and whether the collectors are feeding us. -->
	<section class="grid gap-4 md:grid-cols-2">
		<Card>
			<CardHeader title="Open detections" level={2}>
				{#snippet actions()}
					<Button href="/detections" size="sm" variant="ghost" onclick={followLink}>View all →</Button>
				{/snippet}
			</CardHeader>
			{#if detectionItems.length === 0}
				<EmptyState
					icon={ShieldCheck}
					title="Nothing open"
					description="The lights are green. New detections land here as the engine files them."
				/>
			{:else}
				<ul class="flex flex-col">
					{#each detectionItems as d (d.id)}
						<li class="flex items-center justify-between gap-3 border-b border-border-soft py-2 last:border-b-0">
							<a href={`/detections/${d.id}`} use:link class="link truncate text-[13px]">{d.title}</a>
							<div class="flex shrink-0 items-center gap-2">
								<SeverityBadge severity={d.severity} />
								<span class="font-mono text-xs text-text-dim">{fmtRelative(d.last_seen_at)}</span>
							</div>
						</li>
					{/each}
				</ul>
			{/if}
		</Card>

		<Card>
			<CardHeader title="Source health" level={2}>
				{#snippet actions()}
					<Button href="/sources" size="sm" variant="ghost" onclick={followLink}>Manage →</Button>
				{/snippet}
			</CardHeader>
			{#if sourceItems.length === 0}
				<EmptyState
					icon={RadioTower}
					title="No sources yet"
					description="Register a collector from Sources to start receiving events."
				/>
			{:else}
				<ul class="flex flex-col">
					{#each sourceItems as s (s.id)}
						{@const h = sourceHealth(s.last_seen_at)}
						<li class="flex items-center justify-between gap-3 border-b border-border-soft py-2 text-[13px] last:border-b-0">
							<span class="flex min-w-0 items-center gap-2.5">
								<StatusDot tone={HEALTH_TONE[h]} label={h} />
								<span class="truncate font-mono text-text">{s.name}</span>
								<span class="truncate text-xs text-text-faint">{s.source_type}</span>
							</span>
							<span class="shrink-0 font-mono text-xs {HEALTH_TEXT[h]}">
								{fmtRelative(s.last_seen_at)}
							</span>
						</li>
					{/each}
				</ul>
			{/if}
		</Card>
	</section>
{/if}
